import {
  getFunctions,
  httpsCallable,
  type Functions,
} from 'firebase/functions';
import { FirebaseError } from 'firebase/app';
import type { Role } from '@mct/shared';
import { getFirebaseApp } from './firebase';

export interface InviteUserBody {
  projectId: string;
  email: string;
  role: Role;
}

export interface InviteUserResult {
  ok: true;
  path: 'direct-add' | 'invitation';
  invitationId?: string;
  userId?: string;
}

export interface RevokeInvitationBody {
  invitationId: string;
}

export interface PromoteToAdminBody {
  email: string;
}

export interface RevokeAdminBody {
  uid: string;
}

export interface GenericOkResult {
  ok: true;
}

export type CallableName =
  | 'inviteUserToProject'
  | 'revokeInvitation'
  | 'promoteToAdmin'
  | 'revokeAdmin'
  | 'listBacklogProjects'
  | 'listAllBacklogUsers'
  | 'saveBacklogApiKey'
  | 'runScheduledJobNow';

export interface BacklogProjectListItem {
  id: number;
  projectKey: string;
  name: string;
  archived: boolean;
}

export type ListBacklogProjectsResult =
  | { ok: true; projects: BacklogProjectListItem[] }
  | {
      ok: false;
      reason: 'config-missing' | 'secret-error' | 'backlog-error';
      message: string;
    };

export interface BacklogUserListItem {
  id: number;
  name: string;
  mailAddress?: string;
  /** Backlog project keys this user was found under (deduped, sorted). */
  sourceProjects: string[];
}

export type ListAllBacklogUsersResult =
  | { ok: true; users: BacklogUserListItem[] }
  | {
      ok: false;
      reason: 'config-missing' | 'secret-error' | 'backlog-error';
      message: string;
    };

export type CallableRunner = <Req, Res>(
  name: CallableName,
  body: Req,
) => Promise<Res>;

let _runner: CallableRunner | null = null;
let _fns: Functions | null = null;

export function setAdminCallableRunnerForTests(
  runner: CallableRunner | null,
): CallableRunner | null {
  const prev = _runner;
  _runner = runner;
  return prev;
}

function defaultRunner(): CallableRunner {
  return async <Req, Res>(name: CallableName, body: Req): Promise<Res> => {
    if (!_fns) {
      _fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    }
    const callable = httpsCallable<Req, Res>(_fns, name);
    const res = await callable(body);
    return res.data;
  };
}

async function run<Req, Res>(name: CallableName, body: Req): Promise<Res> {
  const runner = _runner ?? defaultRunner();
  return runner<Req, Res>(name, body);
}

export async function inviteUserToProject(
  projectId: string,
  email: string,
  role: Role,
): Promise<InviteUserResult> {
  // The deployed Cloud Function is exported as `inviteUserToProject`
  // in functions/src/index.ts; the callable name MUST match the deploy
  // name or the request resolves to a non-existent URL and the browser
  // surfaces it as a CORS preflight failure (no Access-Control-Allow-
  // Origin on the empty response).
  return run<InviteUserBody, InviteUserResult>('inviteUserToProject', {
    projectId,
    email: email.trim().toLowerCase(),
    role,
  });
}

export async function revokeInvitation(
  invitationId: string,
): Promise<GenericOkResult> {
  return run<RevokeInvitationBody, GenericOkResult>('revokeInvitation', {
    invitationId,
  });
}

export async function promoteToAdmin(email: string): Promise<GenericOkResult> {
  return run<PromoteToAdminBody, GenericOkResult>('promoteToAdmin', {
    email: email.trim().toLowerCase(),
  });
}

export async function revokeAdmin(uid: string): Promise<GenericOkResult> {
  return run<RevokeAdminBody, GenericOkResult>('revokeAdmin', { uid });
}

export async function listBacklogProjects(
  projectId: string,
): Promise<ListBacklogProjectsResult> {
  return run<{ projectId: string }, ListBacklogProjectsResult>(
    'listBacklogProjects',
    { projectId },
  );
}

/**
 * Fetch the union of Backlog users across every Backlog project the MCT
 * project tracks. Powers the Settings page "Backlog ユーザー" picker.
 */
export async function listAllBacklogUsers(
  projectId: string,
): Promise<ListAllBacklogUsersResult> {
  return run<{ projectId: string }, ListAllBacklogUsersResult>(
    'listAllBacklogUsers',
    { projectId },
  );
}

export interface SaveBacklogApiKeyBody {
  projectId: string;
  apiKey: string;
}

export interface SaveBacklogApiKeyResult {
  ok: true;
  /** Deterministic Secret Manager name; UI uses it to drive listBacklogProjects. */
  secretName: string;
  /** True when the underlying secret was newly created vs. addVersion-ed. */
  created: boolean;
}

export async function saveBacklogApiKey(
  projectId: string,
  apiKey: string,
): Promise<SaveBacklogApiKeyResult> {
  return run<SaveBacklogApiKeyBody, SaveBacklogApiKeyResult>(
    'saveBacklogApiKey',
    { projectId, apiKey },
  );
}

/**
 * Pre-migration prod docs sometimes have the raw API key body stored in
 * `backlog.apiKeySecretName` (the bug we're fixing). The canonical
 * shape is always `backlog-api-key-{projectId}`. UI surfaces a recovery
 * banner whenever the saved value doesn't match — see SettingsPage.
 */
export function looksLikeLegacyBacklogSecretName(value: string | undefined): boolean {
  if (!value) return false;
  if (!value.startsWith('backlog-api-key-')) return true;
  return value.length > 120;
}

/**
 * Wire format for the manual-kick callable. Mirrors the server's
 * `RunScheduledJobNowResult` discriminated union — the `ok: false`
 * variant covers the only two predictable failure modes (no doc, no
 * registered handler); HTTPS errors (auth, project not found) come
 * through `translateCallableError` instead.
 */
export type RunScheduledJobNowResult =
  | {
      ok: true;
      outcome:
        | {
            status: 'success' | 'partial';
            runId: string;
            durationMs: number;
            itemsProcessed: number | null;
            summary?: string;
          }
        | {
            status: 'failed';
            runId: string;
            durationMs: number;
            itemsProcessed: number | null;
            error: string;
          };
    }
  | { ok: false; reason: 'no-doc' | 'handler-missing'; message: string };

export async function runScheduledJobNow(
  projectId: string,
  jobId: string,
): Promise<RunScheduledJobNowResult> {
  return run<
    { projectId: string; jobId: string },
    RunScheduledJobNowResult
  >('runScheduledJobNow', { projectId, jobId });
}

/** Callable error translator ------------------------------------------- */

export interface TranslatedCallableError {
  code: string;
  message: string;
}

const CODE_MESSAGES: Record<string, string> = {
  'functions/permission-denied': '権限がありません。管理者にご連絡ください。',
  'functions/unauthenticated': 'サインインが必要です。',
  'functions/not-found':
    '対象のユーザーが見つかりません。対象のメールアドレスで一度サインインしてもらってください。',
  'functions/already-exists': 'すでに招待済み、または登録済みです。',
  'functions/failed-precondition':
    '操作を実行できません（最後の管理者は権限を外せません）。',
  'functions/invalid-argument': '入力内容が正しくありません。',
  'functions/resource-exhausted': '一時的に利用が集中しています。時間をおいて再度お試しください。',
  'functions/unavailable': 'サーバーに接続できません。ネットワークを確認してください。',
  'functions/internal': 'サーバーエラーが発生しました。',
};

export function translateCallableError(err: unknown): TranslatedCallableError {
  if (err instanceof FirebaseError) {
    return {
      code: err.code,
      message: CODE_MESSAGES[err.code] ?? err.message,
    };
  }
  if (err instanceof Error) {
    return { code: 'unknown', message: err.message };
  }
  return { code: 'unknown', message: String(err) };
}
